// src/components/dashboard/DailyTimeline.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../config/firebase';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import TimelineEvent from './TimelineEvent';
import TimelineTodo from './TimelineTodo';
import '../../../styles/DailyTimeline.css';

const HOURS = Array.from({ length: 18 }, (_, i) => 6 + i); // 06:00 to 23:00

const DailyTimeline = () => {
    const [events, setEvents] = useState([]);
    const [todos, setTodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const navigate = useNavigate();
    const currentUser = auth.currentUser;

    // Helper function to filter and set todos for selected date
    const filterAndSetTodos = (newTodos, isCurrentUser) => {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        const todosForDay = newTodos.filter(todo => {
            if (!todo.dueDate) return false;
            const todoDate = new Date(todo.dueDate);
            todoDate.setHours(0, 0, 0, 0);
            const compareDate = new Date(startOfDay);
            compareDate.setHours(0, 0, 0, 0);
            return todoDate.getTime() === compareDate.getTime();
        });

        todosForDay.sort((a, b) => {
            if (a.time && b.time) {
                return a.time.localeCompare(b.time);
            }
            const priorityOrder = { high: 0, medium: 1, low: 2 };
            return (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3);
        });

        const formattedTodos = todosForDay.map(todo => ({
            ...todo,
            id: todo.id,
            title: todo.todo,
            priority: todo.priority || 'medium',
            time: todo.time || '09:00',
            status: todo.status || false,
            assignee: todo.assigneeName || (todo.isCurrentUser ? 'You' : 'Partner'),
            owner: todo.isCurrentUser ? 'self' : 'partner'
        }));

        // Update todos state
        setTodos(prevTodos => {
            const otherTodos = prevTodos.filter(todo => 
                isCurrentUser ? todo.owner !== 'self' : todo.owner !== 'partner'
            );
            return [...otherTodos, ...formattedTodos];
        });
    };

    useEffect(() => {
        if (!currentUser) {
            setLoading(false);
            return;
        }
        
        const unsubscribers = [];
        
        const setupRealTimeListeners = async () => {
            try {
                // Get user info to find partner
                const userInfoDoc = await getDoc(doc(db, "UserInformation", currentUser.uid));
                let partnerId = null;
                
                if (userInfoDoc.exists()) {
                    const userData = userInfoDoc.data();
                    partnerId = userData.inRelationWith;
                }

                // Set up listener for current user's events
                const userEventsRef = collection(db, "events", currentUser.uid, "userEvents");
                const userEventsListener = onSnapshot(userEventsRef, (snapshot) => {
                    const userEvents = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        userEvents.push({
                            id: doc.id,
                            ...data,
                            start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                            end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                            userId: currentUser.uid,
                            owner: 'self'
                        });
                    });

                    // Filter events for selected date
                    const startOfDay = new Date(selectedDate);
                    startOfDay.setHours(0, 0, 0, 0);
                    const endOfDay = new Date(selectedDate);
                    endOfDay.setHours(23, 59, 59, 999);

                    const eventsForDay = userEvents.filter(event => {
                        if (!event.start) return false;
                        const eventDate = new Date(event.start);
                        return eventDate >= startOfDay && eventDate <= endOfDay;
                    });

                    // Update events state
                    setEvents(prevEvents => {
                        const partnerEvents = prevEvents.filter(event => event.owner === 'partner');
                        const allEvents = [...eventsForDay, ...partnerEvents];
                        allEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
                        return allEvents;
                    });
                    
                    setLoading(false);
                }, (error) => {
                    console.error("Error listening to user events:", error);
                    setLoading(false);
                });
                unsubscribers.push(userEventsListener);

                // Set up listener for partner's events if partner exists
                if (partnerId) {
                    const partnerEventsRef = collection(db, "events", partnerId, "userEvents");
                    const partnerEventsListener = onSnapshot(partnerEventsRef, (snapshot) => {
                        const partnerEvents = [];
                        snapshot.forEach(doc => {
                            const data = doc.data();
                            partnerEvents.push({
                                id: doc.id,
                                ...data,
                                start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                                end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                                userId: partnerId,
                                owner: 'partner'
                            });
                        });

                        // Filter events for selected date
                        const startOfDay = new Date(selectedDate);
                        startOfDay.setHours(0, 0, 0, 0);
                        const endOfDay = new Date(selectedDate);
                        endOfDay.setHours(23, 59, 59, 999);

                        const eventsForDay = partnerEvents.filter(event => {
                            if (!event.start) return false;
                            const eventDate = new Date(event.start);
                            return eventDate >= startOfDay && eventDate <= endOfDay;
                        });

                        // Update events state
                        setEvents(prevEvents => {
                            const userEvents = prevEvents.filter(event => event.owner === 'self');
                            const allEvents = [...userEvents, ...eventsForDay];
                            allEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
                            return allEvents;
                        });
                    }, (error) => {
                        console.error("Error listening to partner events:", error);
                    });
                    unsubscribers.push(partnerEventsListener);
                }

                // Set up listener for current user's todos
                const userTodosRef = collection(db, "events", currentUser.uid, "userTodos");
                const userTodosListener = onSnapshot(userTodosRef, (snapshot) => {
                    const userTodos = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        const dueDate = data.date ? new Date(data.date) : null;
                        userTodos.push({
                            ...data,
                            id: doc.id,
                            dueDate: dueDate,
                            isCurrentUser: true
                        });
                    });

                    // Filter todos for selected date and update state
                    filterAndSetTodos(userTodos, true);
                }, (error) => {
                    console.error("Error listening to user todos:", error);
                });
                unsubscribers.push(userTodosListener);

                // Set up listener for partner's todos if partner exists
                if (partnerId) {
                    const partnerTodosRef = collection(db, "events", partnerId, "userTodos");
                    const partnerTodosListener = onSnapshot(partnerTodosRef, (snapshot) => {
                        const partnerTodos = [];
                        snapshot.forEach(doc => {
                            const data = doc.data();
                            const dueDate = data.date ? new Date(data.date) : null;
                            partnerTodos.push({
                                ...data,
                                id: doc.id,
                                dueDate: dueDate,
                                isCurrentUser: false
                            });
                        });

                        // Filter todos for selected date and update state
                        filterAndSetTodos(partnerTodos, false);
                    }, (error) => {
                        console.error("Error listening to partner todos:", error);
                    });
                    unsubscribers.push(partnerTodosListener);
                }

            } catch (error) {
                console.error("Error setting up real-time listeners:", error);
                setLoading(false);
            }
        };

        setupRealTimeListeners();

        // Return cleanup function
        return () => {
            unsubscribers.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
        };
    }, [currentUser, selectedDate]);

    const generateRecurringDates = (template) => {
        if (!template.repeat || !template.start) return [template];
        const occurrences = [template];
        const start = new Date(template.start);
        const repeatUntil = template.repeatUntil || new Date(new Date().setMonth(new Date().getMonth() + 3));
        let currentDate = new Date(start);
        while (currentDate < repeatUntil) {
            currentDate = new Date(currentDate);
            switch (template.repeatType) {
                case 'daily':
                    currentDate.setDate(currentDate.getDate() + 1);
                    break;
                case 'weekly':
                    currentDate.setDate(currentDate.getDate() + 7);
                    break;
                case 'biweekly':
                    currentDate.setDate(currentDate.getDate() + 14);
                    break;
                case 'monthly':
                    currentDate.setMonth(currentDate.getMonth() + 1);
                    break;
                case 'yearly':
                    currentDate.setFullYear(currentDate.getFullYear() + 1);
                    break;
                default:
                    return occurrences;
            }
            if (currentDate <= repeatUntil) {
                const endDate = template.end ? new Date(currentDate.getTime() + (template.end - template.start)) : null;
                occurrences.push({
                    ...template,
                    start: new Date(currentDate),
                    end: endDate,
                    generated: true
                });
            }
        }
        return occurrences;
    };

    const navigateToCalendar = () => {
        navigate('/dashboard/organization/calendar');
    };

    const formatTimeString = (date) => {
        const dateObj = date instanceof Date ? date : new Date(date);
        return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handlePrevDay = () => {
        const prevDay = new Date(selectedDate);
        prevDay.setDate(prevDay.getDate() - 1);
        setSelectedDate(prevDay);
    };
    const handleNextDay = () => {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setSelectedDate(nextDay);
    };
    // Large date header helpers
    const getDayString = (date) => date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    const getWeekdayString = (date) => date.toLocaleString('en-US', { weekday: 'long' });

    // Separate all-day events
    const allDayEvents = events.filter(e => e.allDay === true || (
        e.start && e.end && new Date(e.start).getHours() === 0 && new Date(e.end).getHours() === 23
    ));
    // Only show non-all-day events in the time grid
    const timedEvents = events.filter(e => !allDayEvents.includes(e));

    // Helper: get minutes since 06:00
    const getMinutesSinceStart = (date) => {
        const d = new Date(date);
        return (d.getHours() - 6) * 60 + d.getMinutes();
    };
    // Helper: get event block style (top, height)
    const getEventBlockStyle = (event) => {
        const totalMinutes = (23 - 6 + 1) * 60; // 06:00 to 23:59
        const start = getMinutesSinceStart(event.start);
        const end = event.end ? getMinutesSinceStart(event.end) : start + 60;
        const top = (start / totalMinutes) * 100;
        const height = ((end - start) / totalMinutes) * 100;
        return {
            position: 'absolute',
            left: '0',
            right: '0',
            top: `${top}%`,
            height: `${height}%`,
            background: event.backgroundColor || '#F44336',
            color: '#fff',
            borderRadius: '0px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        };
    };

    return (
        <div className="daily-timeline new-layout">
            {/* Large date header */}
            <div className="big-date-header">
                <button className="big-nav-arrow" onClick={handlePrevDay}>&lt;</button>
                <div>
                    <div className="big-date">{getDayString(selectedDate)}</div>
                    <div className="big-weekday">{getWeekdayString(selectedDate)}</div>
                </div>
                <button className="big-nav-arrow" onClick={handleNextDay}>&gt;</button>
            </div>
            {/* Scrollable main content */}
            <div className="daily-scrollable-content">
                {/* All Day section */}
                <div className="all-day-section">
                    <div className="all-day-label">All Day</div>
                    <div className="all-day-events">
                        {allDayEvents.length === 0 ? (
                            <div className="no-all-day">No all-day events</div>
                        ) : (
                            allDayEvents.map(event => (
                                <div className="all-day-block" key={event.id} style={{ background: event.backgroundColor || 'linear-gradient(90deg, #6a85f1, #a777e3)'}}>
                                    <div className="all-day-title">{event.title}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                {/* Time grid with absolute event overlay */}
                <div className="time-grid absolute-grid">
                    <div className="time-labels-column">
                        {HOURS.map(hour => (
                            <div className="time-label" key={hour}>{hour.toString().padStart(2, '0')}:00</div>
                        ))}
                    </div>
                    <div className="event-overlay-area">
                        {timedEvents.map(event => (
                            <div className="event-block-absolute" key={event.id} style={getEventBlockStyle(event)}>
                                {/* Only show event title, not time */}
                                <div>{event.title}</div>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Tasks section */}
                <div className="tasks-section">
                    <div className="tasks-label">Tasks</div>
                    <div className="tasks-list">
                        {todos.length === 0 ? (
                            <div className="no-tasks">No tasks for today</div>
                        ) : (
                            todos.map(todo => (
                                <div className={`task-item${todo.status ? ' completed' : ''}`} key={todo.id}>
                                    <div className="task-title" style={{ textDecoration: todo.status ? 'line-through' : 'none' }}>{todo.todo}</div>
                                    <div className="task-category">{todo.category}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
            {/* View Full Calendar button */}
            <button onClick={navigateToCalendar} className="view-full-calendar-btn">
                View Full Calendar
            </button>
        </div>
    );
};

export default DailyTimeline;
